import { Box, List, Stack, Typography } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import type { GroupingField, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch";
import { selectTabularGrouping } from "../../../../store/selectors/fieldsStateSelectors";
import { fieldsStateActions } from "../../../../store/thunks/fieldStatesThunks";
import SelectGroupFieldInput from "../../common/sorting/SelectGroupFieldInput";
import GroupFieldItem from "./GroupFieldItem";

interface Props {
  availableFields: ReportField[];
  readonlyMode: boolean;
}

export function GroupsDropFieldsContainer({ availableFields, readonlyMode }: Props) {
  const groupAreaValues = useSelector(selectTabularGrouping);
  const dispatch = useAppDispatch();

  const [groups, setGroups] = useState<GroupingField[]>([]);
  const fieldsRef = useRef(groups);
  useEffect(() => {
    fieldsRef.current = groups;
  }, [groups]);

  useEffect(() => {
    //eslint-disable-next-line @eslint-react/set-state-in-effect
    setGroups(groupAreaValues);
  }, [groupAreaValues, setGroups]);

  const onRemoveItem = useCallback(
    (field: GroupingField) => {
      dispatch(fieldsStateActions.removeGroup(field));
    },
    [dispatch]
  );

  const onDragMove = useCallback(
    (item: GroupingField, replaceItem: GroupingField) => {
      const values = [...fieldsRef.current];
      const fieldIndex = values.findIndex((v) => v.id === item.id);
      const toIndex = values.findIndex((v) => v.id === replaceItem.id);
      if (fieldIndex > -1) {
        values.splice(fieldIndex, 1);
        values.splice(toIndex, 0, item);
        setGroups(values);
      }
    },
    [setGroups]
  );

  const onEndMoveItem = useCallback(
    (field: GroupingField) => {
      const newIndex = fieldsRef.current.findIndex((v) => v.id === field.id);
      dispatch(fieldsStateActions.moveGroup({ field, newIndex }));
    },
    [dispatch]
  );

  return (
    <Box
      sx={() => ({
        display: "flex",
        flexDirection: "column",
        flex: 1,
        gap: ".5rem",
      })}
    >
      <Typography variant="subtitle2" sx={{ color: (theme) => theme.palette.text.primary }}>
        Groups
      </Typography>
      <Stack
        sx={(theme) => ({
          backgroundColor: theme.palette.background.grey,
          minHeight: "90px",
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "2px",
          p: ".5rem",
          gap: ".5rem",
        })}
      >
        {groups.length > 0 && (
          <List sx={{ p: 0, display: "flex", flexDirection: "column", gap: "5px" }}>
            {groups.map((item) => {
              return (
                <GroupFieldItem
                  key={item.id}
                  field={item}
                  onRemoveItem={onRemoveItem}
                  onDragMove={onDragMove}
                  onEndMoveItem={onEndMoveItem}
                  readonlyMode={readonlyMode}
                />
              );
            })}
          </List>
        )}
        <SelectGroupFieldInput availableFields={availableFields} disabled={readonlyMode} />
      </Stack>
    </Box>
  );
}

export default GroupsDropFieldsContainer;
